import React, { useState } from 'react';
import { usePOSStore } from '../../stores/pos.store';
import { useBaseCurrency } from '../../db/hooks/useLookupData';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { ArrowRight, Trash2 } from 'lucide-react';
import { useOverlayDepth } from '../../lib/overlayStack';

export function POSFloatingDraftDock() {
  const {
    isOpen,
    isStaffFormOpen,
    cartItems,
    customer,
    resume,
    discardDraft,
    discount,
    tip,
  } = usePOSStore();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false);
  const overlayDepth = useOverlayDepth();
  const hasDraft = cartItems.length > 0 || customer !== null;

  // The dock pill is shown only when:
  // 1. POS is NOT open
  // 2. Staff form is NOT open
  // 3. No OTHER drawer/modal is open (if confirm discard dialog is open, overlayDepth is 1)
  // 4. There is an active draft (cart items or customer)
  const otherOverlaysOpen = isConfirmDiscardOpen ? overlayDepth > 1 : overlayDepth > 0;
  const shouldShowDockPill = !isOpen && !isStaffFormOpen && !otherOverlaysOpen && hasDraft;

  // If there is no draft at all and confirm dialog is closed, unmount
  if (!hasDraft && !isConfirmDiscardOpen) {
    return null;
  }

  const subtotal = cartItems.reduce(
    (acc, item) => acc + Math.max(0, item.unitPrice - (item.discountAmount || 0)) * item.quantity,
    0
  );
  const netPayable = subtotal - discount + tip;

  return (
    <>
      {shouldShowDockPill && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[50] w-[calc(100%-2rem)] max-w-xl bg-surface-secondary/95 backdrop-blur-xl border border-border shadow-2xl rounded-2xl p-3 sm:px-4 flex items-center justify-between gap-3 animate-slide-up">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange font-bold text-xs shrink-0">
            {cartItems.length}
          </div>
          <div className="min-w-0">
            <span className="text-xs font-black text-text-primary block truncate">
              Venta en Pausa · {customer ? customer.name : 'Cliente Mostrador'}
            </span>
            {cartItems.length > 0 ? (
              <span className="text-[11px] font-extrabold text-brand-orange block">
                {currencySymbol}{Math.max(0, netPayable).toFixed(2)} {baseCurrency}
              </span>
            ) : (
              <span className="text-[11px] font-bold text-text-tertiary block">
                Sin servicios añadidos
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => setIsConfirmDiscardOpen(true)}
            className="p-2 text-text-tertiary hover:text-error hover:bg-error/10 rounded-xl transition-colors cursor-pointer"
            title="Descartar venta"
            aria-label="Descartar venta en pausa"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={resume}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-brand-orange hover:bg-brand-orange/90 text-white text-xs font-extrabold shadow-md shadow-brand-orange/20 transition-all cursor-pointer"
            aria-label="Reanudar venta"
          >
            <span>Reanudar</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      )}

      <ConfirmDialog
        isOpen={isConfirmDiscardOpen}
        onClose={() => setIsConfirmDiscardOpen(false)}
        onConfirm={async () => {
          discardDraft();
          setIsConfirmDiscardOpen(false);
        }}
        title="¿Descartar venta en pausa?"
        message="Se perderán los servicios y productos agregados a este carrito."
        confirmText="Descartar"
        variant="danger"
      />
    </>
  );
}
